import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Database, Plus, X, CheckCircle2, AlertCircle, Loader2, Sparkles, RefreshCw } from 'lucide-react';
import Button from '../../components/ui/Button';
import Card, { CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Tooltip from '../../components/ui/Tooltip';
import { useAnalysisStore, EnterpriseSystem } from '../../stores/analysisStore';
import { api, caseAnalysisAPI } from '../../lib/api';
import {
  knowledgeGraphDesignApi,
  getEnterpriseSystemsByIndustry,
  getAvailableEnterpriseSystems,
  recommendSystemsForCase,
} from '../../lib/api/knowledgeGraphDesignApi';
import type { SystemRecommendation } from '../../lib/api/knowledgeGraphDesignApi';

interface Step2Props {
  caseId: string;
  onNext: () => void;
  onBack: () => void;
}

export const Step2EnterpriseSystemsSelection: React.FC<Step2Props> = ({ caseId, onNext, onBack }) => {
  const {
    enterpriseSystems,
    customSystems,
    setEnterpriseSystems,
    toggleSystemSelection,
    addCustomSystem,
    removeCustomSystem,
  } = useAnalysisStore();

  // Track if we've initialized the systems
  const [initialized, setInitialized] = useState(false);
  const queryClient = useQueryClient();

  // Fetch the case so we know which industry to filter / generate systems for.
  // The Case node stores the industry on the linked Company (`comp.industry`)
  // or in `wizardData`, and the case detail endpoint exposes it.
  const { data: caseData, isLoading: loadingCase } = useQuery<any>({
    queryKey: ['case', caseId],
    queryFn: () => api.get(`/cases/${caseId}`),
    enabled: !!caseId,
    staleTime: 5 * 60 * 1000,
  });

  // Industry resolution.  Prefer the company.industry; fall back to the
  // industryData snapshot inside wizardData (set during the discovery wizard).
  const industry = useMemo<string | null>(() => {
    if (!caseData) return null;
    const c = caseData as any;
    const fromCompany =
      c?.company?.industry ||
      c?.companyData?.industry ||
      c?.linkedCompany?.industry;
    const fromWizard =
      c?.wizardData?.industryData?.name ||
      c?.industryData?.name ||
      c?.industry;
    return (fromCompany || fromWizard || null) as string | null;
  }, [caseData]);

  // ── System catalog: union of two sources ─────────────────────────────────
  //
  // 1) Industry-scoped systems — new endpoint that returns systems tagged with
  //    the case's industry; LLM-generates 8-12 fresh ones on first visit for
  //    an industry that has no coverage yet.
  // 2) Full global library — same endpoint dev's Step 2 used.  Returns all
  //    ~30 well-researched seed systems (SAP S/4HANA, Ariba, Oracle ERP,
  //    Salesforce, etc.) regardless of industry tag.
  //
  // We render BOTH so the user never loses access to the rich global library
  // (parity with dev), while still seeing curated industry-relevant systems
  // surfaced first.  Without (2), Amazon (Retail-Catalog & Mail-Order Houses)
  // — which has no system pre-tagged with that exact industry string — would
  // only see 10 LLM-generated synthetic systems instead of the 30 rich seeds
  // dev used to show.
  //
  // Industry-scoped query — gated on `industry` so we don't fire it when
  // the case has no industry resolved yet.
  const {
    data: availableSystemsResp,
    isFetching: refetchingSystems,
    isLoading: loadingByIndustry,
    isError: byIndustryError,
    refetch: refetchSystems,
  } = useQuery({
    queryKey: ['enterprise-systems-by-industry', industry],
    queryFn: () => getEnterpriseSystemsByIndustry(industry as string),
    enabled: !!industry,
    staleTime: 60 * 60 * 1000,  // 1 hour — these change rarely
    retry: 1,
  });

  // Full library query — always runs (matches dev's Step 2 behaviour).
  const {
    data: allSystemsResp,
    isLoading: loadingAllSystems,
    error: allSystemsError,
  } = useQuery({
    queryKey: ['available-enterprise-systems'],
    queryFn: () => getAvailableEnterpriseSystems({ pageSize: 100 }),
    staleTime: 5 * 60 * 1000,
  });

  // The "still loading the picker" gate.
  //
  // This used to track ONLY the global library, treating the industry-scoped
  // query as purely additive. But on a new industry that query calls an LLM and
  // takes ~30 s, while the library returns in milliseconds — so the store was
  // populated from the library alone, `systemIds` went non-empty, and the
  // server-side recommendation (F11) fired against a system list that was about
  // to be replaced. When the industry systems finally landed the picker showed
  // them with no chips, because the chips had been computed for the earlier ids.
  // A manual refresh "fixed" it only because everything was cached by then.
  //
  // Waiting for the industry query whenever an industry is expected makes the
  // first paint match every later one. `isLoading` (not `isFetching`) so a
  // background revalidation of cached systems never re-shows the skeleton.
  const loadingAvailableSystems = loadingAllSystems || (!!industry && loadingByIndustry);
  // We surface the same error UX as before only when BOTH queries fail
  // (so a transient industry-scoped error doesn't blank the picker).
  const systemsError = allSystemsError;

  // Union: industry-scoped first (so they appear at the top of the picker),
  // then the rest of the global library — with duplicates removed by
  // (vendor, system_name) tuple rather than system_id.  Different sources
  // (LLM by-industry generation, manual seed, library seed) produce fresh
  // random `system_id`s for the *same* logical product, so id-based dedup
  // lets duplicates through.  Name+vendor is the stable identity key.
  const availableSystems = useMemo(() => {
    const industrySystems = availableSystemsResp?.systems ?? [];
    const globalSystems = allSystemsResp?.systems ?? [];
    if (industrySystems.length === 0 && globalSystems.length === 0) return null;

    const normKey = (s: { system_name?: string; vendor?: string }) =>
      `${(s.vendor ?? '').trim().toLowerCase()}::${(s.system_name ?? '').trim().toLowerCase()}`;

    const seen = new Set<string>();
    const merged: typeof industrySystems = [];
    // Industry-scoped wins — append first; then library entries that
    // aren't already represented by the same name+vendor.
    for (const s of industrySystems) {
      const k = normKey(s);
      if (!seen.has(k)) {
        seen.add(k);
        merged.push(s);
      }
    }
    for (const s of globalSystems) {
      const k = normKey(s);
      if (!seen.has(k)) {
        seen.add(k);
        merged.push(s);
      }
    }
    return { systems: merged, total: merged.length };
  }, [availableSystemsResp, allSystemsResp]);

  // Force-regenerate the LLM list (admin-style refresh, e.g., if the
  // generated set looks off).  Archives the previous llm_generated entries
  // for this industry on the backend, then re-runs the LLM.
  //
  // Confirmation is now a themed ConfirmDialog (matches AnalysisWizard's
  // Start-Over flow) instead of window.confirm() so it visually belongs to
  // the app and so the destructive nature ("archive the previous set") is
  // surfaced with a danger variant.
  const [regenerating, setRegenerating] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);

  const handleRegenerate = () => {
    if (!industry) return;
    setShowRegenerateDialog(true);
  };

  const confirmRegenerate = async () => {
    if (!industry) return;
    setShowRegenerateDialog(false);
    setRegenerating(true);
    try {
      const fresh = await getEnterpriseSystemsByIndustry(industry, { forceRegenerate: true });
      queryClient.setQueryData(['enterprise-systems-by-industry', industry], fresh);
      // Drop initialized so the useEffect below rehydrates the picker
      setInitialized(false);
      toast.success(`Regenerated ${fresh.count} systems for ${industry}`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to regenerate systems');
    } finally {
      setRegenerating(false);
    }
  };

  // Fetch previously selected systems from backend
  const { data: savedSystems, isLoading: loadingSavedSystems } = useQuery({
    queryKey: ['selected-systems', caseId],
    queryFn: () => knowledgeGraphDesignApi.getSelectedSystemsForCase(caseId),
    enabled: !!caseId && !initialized,
    // Keep the fetched selection fresh so re-renders (and the StrictMode
    // double-mount in dev) don't revalidate and re-hit the endpoint. The
    // query is already disabled once `initialized` flips true.
    staleTime: 5 * 60 * 1000,
  });

  // Mutation to save selected systems to Neo4j
  const saveSystemsMutation = useMutation({
    mutationFn: () => caseAnalysisAPI.saveSelectedSystems(
      caseId,
      enterpriseSystems.filter(s => s.selected).map(s => s.systemId),
      customSystems
    ),
    onSuccess: () => {
      toast.success('Enterprise systems saved successfully');
      onNext();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to save enterprise systems');
    }
  });

  const handleContinue = () => {
    saveSystemsMutation.mutate();
  };

  const [customSystemName, setCustomSystemName] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Initialize / re-hydrate the picker from available systems + saved selections.
  //
  // Two behaviours, both important:
  //  1. We flip `initialized` as soon as the GATING queries (global library +
  //     saved selections) settle — even when there are zero systems yet. This
  //     decouples the step from the additive, slow `by-industry` LLM query: an
  //     empty global library + a pending/failed by-industry must NOT leave the
  //     step stuck on a skeleton forever (that was the hang). Worst case the
  //     user sees an empty picker with "Add custom system" + a Retry.
  //  2. We re-run when `availableSystems` changes, so when the by-industry list
  //     resolves LATER it gets merged in without blocking. Selections already
  //     toggled this session are preserved (read from the store via getState so
  //     we don't add `enterpriseSystems` to the deps and loop).
  useEffect(() => {
    if (loadingSavedSystems || loadingAvailableSystems) return; // gating queries still in flight

    const savedSystemsList = savedSystems?.selected_systems || [];
    const savedSystemIds = new Set(
      savedSystemsList.map((s: { system_id: string }) => s.system_id)
    );
    const sessionSelected = new Set(
      useAnalysisStore.getState().enterpriseSystems.filter(s => s.selected).map(s => s.systemId)
    );

    // Carry through enrichment fields (source, target_industries, total_tables).
    // The "Recommended" chip itself is scored server-side (see below).
    const systemsFromAPI: EnterpriseSystem[] = (availableSystems?.systems ?? [])
      .filter(sys => sys.system_id) // Only include systems with valid IDs
      .map(sys => ({
        systemId: sys.system_id,
        name: sys.system_name,
        type: (sys.category || 'ERP') as EnterpriseSystem['type'],
        vendor: sys.vendor,
        version: sys.version,
        selected: savedSystemIds.has(sys.system_id) || sessionSelected.has(sys.system_id),
        source: (sys as any).source,
        targetIndustries: (sys as any).target_industries,
        totalTables: (sys as any).total_tables,
      }));

    setEnterpriseSystems(systemsFromAPI);
    if (!initialized) setInitialized(true);
  }, [initialized, loadingSavedSystems, loadingAvailableSystems, savedSystems, availableSystems, setEnterpriseSystems]);

  const handleAddCustomSystem = () => {
    if (customSystemName.trim()) {
      addCustomSystem(customSystemName.trim());
      setCustomSystemName('');
      setShowCustomInput(false);
    }
  };

  const selectedCount = enterpriseSystems.filter(s => s.selected).length + customSystems.length;
  const canProceed = selectedCount > 0;

  // Group systems by type
  const groupedSystems = enterpriseSystems.reduce((acc, system) => {
    if (!acc[system.type]) {
      acc[system.type] = [];
    }
    acc[system.type].push(system);
    return acc;
  }, {} as Record<string, EnterpriseSystem[]>);

  // Per-category "Recommended" chip. Scored SERVER-SIDE against this case
  // (each system's tables/modules are mapped to KG areas and overlapped with
  // the areas the case's strategic questions need) by
  // POST /enterprise-systems/recommend-for-case; this component only renders
  // the payload. The former client scorer used four constants (industry tag,
  // source, table count, vendor) that tied every generated system at 0.715,
  // so the chip landed on whichever system the API listed first.
  //
  // Keyed on the visible system ids so the chips line up with the cards;
  // toggling a selection does not change the key, so it never refetches.
  const systemIds = useMemo(
    () => enterpriseSystems.map((s) => s.systemId).filter(Boolean).sort(),
    [enterpriseSystems],
  );
  const systemIdsJoined = systemIds.join(',');
  // Loading or error simply means no chip; the picker never blocks on this.
  const { data: recommendation } = useQuery({
    queryKey: ['systems-recommendation', caseId, systemIdsJoined],
    queryFn: () => recommendSystemsForCase(caseId, systemIds),
    enabled: !!caseId && systemIds.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const recommendedByCategory = recommendation?.recommended_by_category ?? {};
  const recommendationBySystem = useMemo(() => {
    const bySystem: Record<string, SystemRecommendation> = {};
    for (const s of recommendation?.systems ?? []) bySystem[s.system_id] = s;
    return bySystem;
  }, [recommendation]);

  // Show loading while fetching systems.  Three distinct loading phases:
  //   1. fetching the Case (need industry context)
  //   2. existing systems lookup OR LLM generation (may take ~30s on first
  //      visit for a new industry — show a different message in that case)
  //   3. waiting on saved-selection rehydration
  if (loadingCase || loadingSavedSystems || loadingAvailableSystems || regenerating || !initialized) {
    const generating = loadingAvailableSystems || regenerating;
    // Skeleton mirrors the loaded layout (industry banner → selection summary →
    // 3 category cards × 6 system tiles → custom systems card) so when the data
    // lands there's no layout jump — the cards "fill in" where their skeletons
    // were.  Keeps the same header + "AI-curating" hint so the user still gets
    // the ~30s expectation message; just no spinning Loader2.
    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">
            Enterprise Systems Selection
          </h2>
          <p className="text-neutral-600">
            {generating && industry
              ? `Looking up commonly-used systems for ${industry}…`
              : 'Loading your system selections…'}
          </p>
          {generating && industry && (
            <>
              <p className="text-xs text-neutral-500 mt-2 max-w-md mx-auto">
                First visit for a new industry takes ~30s while we generate an
                industry-specific list.  Subsequent visits are instant.
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-neutral-500 mt-3">
                <Sparkles className="w-4 h-4 text-violet-500 animate-pulse" />
                <span>AI-curating the system library…</span>
              </div>
            </>
          )}
        </div>

        {/* Industry context banner skeleton — mirrors the violet "N systems
            curated for X" card so the swap-in is seamless. */}
        {generating && industry && (
          <Card className="bg-gradient-to-r from-violet-50 to-white border-violet-200">
            <CardBody>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <div className="w-5 h-5 rounded bg-violet-200 animate-pulse flex-shrink-0 mt-0.5" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-violet-200/70 rounded animate-pulse w-2/3" />
                    <div className="h-3 bg-violet-100 rounded animate-pulse w-1/2" />
                  </div>
                </div>
                <div className="h-8 w-28 bg-violet-100 rounded-md animate-pulse flex-shrink-0" />
              </div>
            </CardBody>
          </Card>
        )}

        {/* Selection summary skeleton — amber summary card placeholder. */}
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50">
          <CardBody>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-6 h-6 rounded bg-amber-200 animate-pulse flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-amber-200/70 rounded animate-pulse w-40" />
                  <div className="h-3 bg-amber-100 rounded animate-pulse w-60" />
                </div>
              </div>
              <div className="w-8 h-8 rounded-full bg-amber-200 animate-pulse flex-shrink-0" />
            </div>
          </CardBody>
        </Card>

        {/* Three category-card skeletons (mirrors ERP / CRM / SCM grouping) with
            a 2-column grid of 6 system-tile placeholders each. */}
        {[0, 1, 2].map((cardIdx) => (
          <Card key={cardIdx}>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="h-5 bg-neutral-200 rounded animate-pulse w-56" />
                <div className="h-5 w-10 bg-neutral-100 rounded-full animate-pulse" />
              </div>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Array.from({ length: 6 }).map((_, tileIdx) => (
                  <div
                    key={tileIdx}
                    className="p-4 rounded-lg border-2 border-neutral-200 bg-white space-y-2"
                  >
                    <div className="h-4 bg-neutral-200 rounded animate-pulse w-32" />
                    <div className="h-3 bg-neutral-100 rounded animate-pulse w-24" />
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        ))}

        {/* Custom systems card skeleton */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="h-5 bg-neutral-200 rounded animate-pulse w-32" />
              <div className="h-8 w-36 bg-neutral-100 rounded-md animate-pulse" />
            </div>
          </CardHeader>
          <CardBody>
            <div className="h-3 bg-neutral-100 rounded animate-pulse w-2/3" />
          </CardBody>
        </Card>
      </div>
    );
  }

  // (Removed previous `if (!industry)` degraded-path card.  Without an
  // industry the industry-scoped query just doesn't fire, but the global
  // library still loads — same picker dev showed.  No need to block.)

  // Error state from systems fetch — show a retry button rather than going silent
  if (systemsError) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">
            Enterprise Systems Selection
          </h2>
        </div>
        <Card className="p-8 border-red-200 bg-red-50">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-base font-semibold text-red-900 mb-1">
                Failed to load systems for {industry}
              </h3>
              <p className="text-sm text-red-800 mb-3">
                {(systemsError as Error)?.message || 'Unknown error.'}
              </p>
              <Button variant="outline" onClick={() => refetchSystems()}>
                Retry
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">
          Enterprise Systems Selection
        </h2>
        <p className="text-neutral-600">
          Select the enterprise systems your organization uses as data sources
        </p>
      </div>

      {/* Industry context banner — shows which industry the list is curated
          for, plus a Regenerate button to re-run the LLM.  Only renders when
          we actually have an industry AND industry-scoped results, so cases
          without industry context look identical to dev's Step 2. */}
      {industry && (availableSystemsResp?.count ?? 0) > 0 && (
        <Card className="bg-gradient-to-r from-violet-50 to-white border-violet-200">
          <CardBody>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3 min-w-0">
                <Sparkles className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-neutral-900">
                    {availableSystemsResp?.count} systems curated for{' '}
                    <span className="text-violet-700">{industry}</span>
                    <span className="ml-2 text-xs font-normal text-neutral-500">
                      (full library also shown below)
                    </span>
                  </p>
                  <p className="text-xs text-neutral-600 mt-0.5">
                    {availableSystemsResp?.source === 'generated'
                      ? 'Freshly generated by AI for this industry.'
                      : 'Cached from earlier AI generation.  Use the Regenerate button to refresh.'}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRegenerate}
                disabled={regenerating || refetchingSystems}
                className="flex items-center gap-2 flex-shrink-0"
              >
                <RefreshCw className={`w-4 h-4 ${regenerating ? 'animate-spin' : ''}`} />
                Regenerate
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Non-blocking by-industry status — the LLM-backed industry list is
          additive, so it must never block the picker. While it generates we show
          an inline hint; if it times out / errors we offer a Retry. Neither gates
          the rest of the step (the user can select systems / add custom ones). */}
      {industry && refetchingSystems && (availableSystemsResp?.count ?? 0) === 0 && (
        <Card className="bg-gradient-to-r from-violet-50 to-white border-violet-200">
          <CardBody>
            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <Sparkles className="w-4 h-4 text-violet-500 animate-pulse flex-shrink-0" />
              <span>
                Looking up commonly-used systems for{' '}
                <span className="font-medium text-violet-700">{industry}</span>… you can keep
                selecting or add your own below.
              </span>
            </div>
          </CardBody>
        </Card>
      )}
      {industry && byIndustryError && !refetchingSystems && (availableSystemsResp?.count ?? 0) === 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardBody>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-2 text-sm text-amber-800 min-w-0">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <span>
                  Couldn’t auto-generate systems for{' '}
                  <span className="font-medium">{industry}</span> in time. You can retry, or just
                  add the systems you use below.
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetchSystems()}
                className="flex items-center gap-2 flex-shrink-0"
              >
                <RefreshCw className="w-4 h-4" />
                Retry
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Selection Summary */}
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50">
        <CardBody>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Database className="w-6 h-6 text-amber-600" />
              <div>
                <p className="font-semibold text-neutral-900">
                  {selectedCount} System{selectedCount !== 1 ? 's' : ''} Selected
                </p>
                <p className="text-sm text-neutral-600">
                  {canProceed ? 'Ready to proceed' : 'Select at least one system to continue'}
                </p>
              </div>
            </div>
            {canProceed ? (
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            ) : (
              <AlertCircle className="w-8 h-8 text-amber-600" />
            )}
          </div>
        </CardBody>
      </Card>

      {/* System Categories */}
      {Object.entries(groupedSystems).map(([type, systems]) => (
        <Card key={type}>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-neutral-900">
                {type === 'ERP' ? 'Enterprise Resource Planning' :
                 type === 'CRM' ? 'Customer Relationship Management' :
                 type === 'SCM' ? 'Supply Chain Management' :
                 type === 'WMS' ? 'Warehouse Management' :
                 type === 'MES' ? 'Manufacturing Execution' : type}
              </h3>
              <Badge variant="secondary">
                {systems.filter(s => s.selected).length}/{systems.length}
              </Badge>
            </div>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {systems.map((system) => {
                const rec = recommendedByCategory[type];
                const isRecommended = rec?.system_id === system.systemId;
                const recDetail = isRecommended ? recommendationBySystem[system.systemId] : undefined;
                // Highlight the recommended (unselected) card with an
                // emerald tint, but if the user actually selects it, the
                // amber selected-state wins (no double-emphasis fight).
                const recHighlight =
                  isRecommended && !system.selected && rec?.confidence === 'strong'
                    ? 'border-emerald-300 bg-emerald-50/30 hover:border-emerald-400'
                    : '';
                return (
                  <button
                    key={system.systemId}
                    onClick={() => toggleSystemSelection(system.systemId)}
                    className={`
                      p-4 rounded-lg border-2 text-left transition-all
                      ${system.selected
                        ? 'border-amber-500 bg-amber-50'
                        : recHighlight
                          ? recHighlight
                          : 'border-neutral-200 hover:border-neutral-300 bg-white'
                      }
                    `}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <p className="font-semibold text-neutral-900">{system.name}</p>
                          {system.selected && (
                            <CheckCircle2 className="w-4 h-4 text-amber-600" />
                          )}
                          {isRecommended && rec && (
                            <Tooltip
                              position="top"
                              variant="light"
                              maxWidth={380}
                              content={
                                <div className="space-y-2">
                                  <p className="font-semibold text-neutral-900">
                                    {rec.confidence !== 'strong'
                                      ? `Top scorer in the ${type} category, but it's a close call`
                                      : rec.system_count === 1
                                        ? `Only ${type} option, and it serves this case's questions`
                                        : `Best fit for ${industry || 'your case'} in the ${type} category`}
                                  </p>
                                  <p className="text-xs text-neutral-600 leading-relaxed">
                                    {recDetail?.recommendation_reason ??
                                      (rec.confidence === 'strong'
                                        ? `This system scored higher than every other ${type} system on the signals below.`
                                        : `This system narrowly beat the other ${type} systems on the signals below. Worth reviewing the alternatives before committing — the gap is small.`)}
                                  </p>
                                  <ul className="text-xs text-neutral-700 list-disc list-outside ml-4 space-y-1">
                                    {(recDetail?.recommendation_signals ?? []).map((s) => (
                                      <li key={s.signal}>
                                        <span className="font-medium">{s.label}</span>
                                        {` (${Math.round(s.value * 100)}%, weight ${Math.round(s.weight * 100)}%): ${s.explanation}`}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              }
                            >
                              <span
                                className={
                                  rec.confidence === 'strong'
                                    ? 'inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 cursor-help'
                                    : 'inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-amber-50 text-amber-800 border border-amber-300 cursor-help'
                                }
                              >
                                <Sparkles className="w-3 h-3" />
                                {rec.confidence === 'strong' ? 'Recommended' : 'Top match'}
                              </span>
                            </Tooltip>
                          )}
                        </div>
                        <p className="text-sm text-neutral-600">
                          {system.vendor}
                          {system.version && ` • ${system.version}`}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardBody>
        </Card>
      ))}

      {/* Custom Systems */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-neutral-900">Custom Systems</h3>
              {customSystems.length > 0 && (
                <Badge variant="secondary">{customSystems.length}</Badge>
              )}
            </div>
            {!showCustomInput && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCustomInput(true)}
              >
                <Plus className="w-4 h-4" />
                Add Custom System
              </Button>
            )}
          </div>
        </CardHeader>
        <CardBody>
          {/* Custom System Input */}
          {showCustomInput && (
            <div className="mb-4 flex gap-2">
              <input
                type="text"
                value={customSystemName}
                onChange={(e) => setCustomSystemName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddCustomSystem()}
                placeholder="Enter custom system name"
                className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                autoFocus
              />
              <Button onClick={handleAddCustomSystem} disabled={!customSystemName.trim()}>
                Add
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCustomInput(false);
                  setCustomSystemName('');
                }}
              >
                Cancel
              </Button>
            </div>
          )}

          {/* Custom Systems List */}
          {customSystems.length > 0 ? (
            <div className="space-y-2">
              {customSystems.map((system, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-amber-50 border-2 border-amber-500 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-amber-600" />
                    <span className="font-medium text-neutral-900">{system}</span>
                    <Badge variant="info">Custom</Badge>
                  </div>
                  <button
                    onClick={() => removeCustomSystem(system)}
                    className="p-1 hover:bg-red-100 rounded transition-colors"
                    title="Remove system"
                  >
                    <X className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            !showCustomInput && (
              <div className="text-center py-8 text-neutral-500">
                <Database className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No custom systems added</p>
                <p className="text-sm">Click "Add Custom System" to add your own systems</p>
              </div>
            )
          )}
        </CardBody>
      </Card>

      {/* Help Text */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">Why select enterprise systems?</p>
            <p>
              The systems you select will be used in the next step to intelligently map your strategic
              questions to specific data tables and columns. This ensures we design a Neo4j schema that
              perfectly aligns with your actual data sources.
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button
          onClick={handleContinue}
          disabled={!canProceed || saveSystemsMutation.isPending}
        >
          {saveSystemsMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : canProceed ? (
            'Continue to Data Mapping'
          ) : (
            'Select at least one system'
          )}
        </Button>
      </div>

      {/* Themed regenerate confirmation — replaces native window.confirm()
          so the destructive nature ("archive the previous set") is surfaced
          properly and the dialog matches the rest of the app. */}
      <ConfirmDialog
        isOpen={showRegenerateDialog}
        title={industry ? `Regenerate enterprise systems for "${industry}"?` : 'Regenerate enterprise systems?'}
        message={[
          'This will archive the previously generated set of systems for this industry and re-run the LLM to produce a fresh list.',
          'Typically takes around 30 seconds.  Library-curated entries are untouched — only AI-generated systems are replaced.',
        ]}
        confirmLabel="Yes, regenerate"
        cancelLabel="Keep current list"
        variant="danger"
        onConfirm={confirmRegenerate}
        onCancel={() => setShowRegenerateDialog(false)}
      />
    </div>
  );
};

export default Step2EnterpriseSystemsSelection;
